import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Eye,
  RotateCcw,
  Square,
  Clock,
  Search,
  RefreshCw,
  Activity,
  CheckCircle,
  XCircle,
  Pause,
  Calendar,
  User,
  Timer,
  Zap,
  TrendingUp,
  AlertTriangle,
  Monitor,
  Play
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { api } from '../lib/api'
import { formatTimestamp, formatDuration } from '../lib/utils'
import { LiveBrowserViewer } from '../components/anchor-browser/LiveBrowserViewer'


// Airflow-inspired status configurations with enhanced visuals
const STATUS_CONFIG = {
  queued: {
    icon: Clock,
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    bgColor: 'bg-slate-50',
    cardColor: 'border-l-slate-400',
    dotColor: 'bg-slate-400',
    label: 'Queued',
    description: 'Waiting to start'
  },
  running: {
    icon: Activity,
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    bgColor: 'bg-blue-50',
    cardColor: 'border-l-blue-500',
    dotColor: 'bg-blue-500',
    label: 'Running',
    description: 'Currently executing'
  },
  success: {
    icon: CheckCircle,
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    bgColor: 'bg-emerald-50',
    cardColor: 'border-l-emerald-500',
    dotColor: 'bg-emerald-500',
    label: 'Success',
    description: 'Completed successfully'
  },
  failed: {
    icon: XCircle,
    color: 'bg-red-100 text-red-700 border-red-300',
    bgColor: 'bg-red-50',
    cardColor: 'border-l-red-500',
    dotColor: 'bg-red-500',
    label: 'Failed',
    description: 'Execution failed'
  },
  cancelled: {
    icon: Pause,
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    bgColor: 'bg-amber-50',
    cardColor: 'border-l-amber-500',
    dotColor: 'bg-amber-500',
    label: 'Cancelled',
    description: 'Manually stopped'
  },
}

export function Runs() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRun, setSelectedRun] = useState<any>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['workflow-runs', page, statusFilter],
    queryFn: () => api.getWorkflowRuns({
      page,
      limit: 50,
      status: statusFilter || undefined
    }),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  })

  // Calculate status counts and metrics
  const runs = data?.runs || []
  const statusCounts = runs.reduce((acc: Record<string, number>, run: any) => {
    acc[run.status] = (acc[run.status] || 0) + 1
    return acc
  }, {})

  // Calculate success rate
  const totalRuns = runs.length
  const successfulRuns = statusCounts.success || 0
  const successRate = totalRuns > 0 ? Math.round((successfulRuns / totalRuns) * 100) : 0

  // Filter runs based on search and status
  const filteredRuns = runs.filter((run: any) => {
    const matchesSearch = !searchQuery ||
      run.workflow_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      run.id.toString().includes(searchQuery)

    const matchesStatus = !statusFilter || run.status === statusFilter

    return matchesSearch && matchesStatus
  })





  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]
    if (!config) return null

    const Icon = config.icon
    return (
      <Badge variant="outline" className={`${config.color} border`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    )
  }

  const getStatusDot = (status: string) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]
    if (!config) return null

    return (
      <div className={`w-3 h-3 rounded-full ${config.dotColor} ${status === 'running' ? 'animate-pulse' : ''}`} />
    )
  }

  const handleRunClick = (run: any) => {
    setSelectedRun(run)
  }

  const handleRetry = async (runId: number) => {
    try {
      await api.retryWorkflowRun(runId)
      refetch()
    } catch (error) {
      console.error('Failed to retry workflow run:', error)
    }
  }

  const handleCancel = async (runId: number) => {
    try {
      await api.cancelWorkflowRun(runId)
      refetch()
    } catch (error) {
      console.error('Failed to cancel workflow run:', error)
    }
  }

  const handleRestart = async (run: any) => {
    try {
      // Start the workflow again using the workflow ID
      await api.startWorkflow(run.workflow_id)
      refetch()
    } catch (error) {
      console.error('Failed to restart workflow:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header - Airflow Style */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Workflow Runs</h1>
                <p className="text-slate-600 mt-1">
                  Real-time monitoring and management of workflow executions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Live Updates
              </div>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Enhanced Metrics Dashboard - Airflow Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Runs */}
          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Total Runs</p>
                  <p className="text-3xl font-bold text-blue-900">{totalRuns}</p>
                  <p className="text-xs text-blue-600 mt-1">All time executions</p>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <TrendingUp className="h-6 w-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Success Rate */}
          <Card className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">Success Rate</p>
                  <p className="text-3xl font-bold text-emerald-900">{successRate}%</p>
                  <p className="text-xs text-emerald-600 mt-1">{successfulRuns} successful</p>
                </div>
                <div className="p-3 bg-emerald-200 rounded-full">
                  <CheckCircle className="h-6 w-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Running Now */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Running Now</p>
                  <p className="text-3xl font-bold text-blue-900">{statusCounts.running || 0}</p>
                  <p className="text-xs text-blue-600 mt-1">Active executions</p>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <Activity className="h-6 w-6 text-blue-700 animate-pulse" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Failed Runs */}
          <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700">Failed Runs</p>
                  <p className="text-3xl font-bold text-red-900">{statusCounts.failed || 0}</p>
                  <p className="text-xs text-red-600 mt-1">Need attention</p>
                </div>
                <div className="p-3 bg-red-200 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Filters and Search */}
        <Card className="bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-slate-900">Filter Runs</h3>
                <Separator orientation="vertical" className="h-6" />
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Showing {filteredRuns.length} of {totalRuns} runs</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search workflows..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64 border-slate-300 focus:border-blue-500"
                  />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
                  <SelectTrigger className="w-40 border-slate-300">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="queued">Queued</SelectItem>
                    <SelectItem value="running">Running</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Runs List - Airflow Style */}
          <div className="lg:col-span-2">
            <Card className="bg-white shadow-sm">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Workflow Runs
                </CardTitle>
                <CardDescription>
                  Real-time execution monitoring and management
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredRuns.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {filteredRuns.map((run: any) => {
                      const config = STATUS_CONFIG[run.status as keyof typeof STATUS_CONFIG]
                      return (
                        <div
                          key={run.id}
                          className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors border-l-4 ${config?.cardColor || 'border-l-slate-300'}`}
                          onClick={() => handleRunClick(run)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {getStatusDot(run.status)}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-900">#{run.id}</span>
                                  <span className="text-slate-600">{run.workflow_name}</span>
                                  {getStatusBadge(run.status)}
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {run.trigger}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatTimestamp(new Date(run.created_at).getTime())}
                                  </span>
                                  {run.duration_ms && (
                                    <span className="flex items-center gap-1">
                                      <Timer className="h-3 w-3" />
                                      {formatDuration(run.duration_ms)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {run.status === 'running' && (
                                <div className="flex items-center gap-2">
                                  <Progress value={65} className="w-20" />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCancel(run.id)
                                    }}
                                  >
                                    <Square className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                              {run.status === 'failed' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRetry(run.id)
                                  }}
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Retry
                                </Button>
                              )}
                              {run.status === 'success' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRestart(run)
                                  }}
                                >
                                  <Play className="h-3 w-3 mr-1" />
                                  Restart
                                </Button>
                              )}
                              <Link to={`/workflow-runs/${run.id}`} onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm">
                                  <Eye className="h-3 w-3" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No runs found</h3>
                    <p className="text-slate-600">
                      {searchQuery ? 'Try adjusting your search criteria.' : 'Workflow runs will appear here once you start executing workflows.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Live Browser Viewer */}
          <div className="lg:col-span-1">
            <Card className="bg-white shadow-sm">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Live Browser Session
                </CardTitle>
                <CardDescription>
                  {selectedRun && selectedRun.status === 'running'
                    ? `Watching ${selectedRun.workflow_name}`
                    : 'Select a running workflow to view live session'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {selectedRun && selectedRun.status === 'running' && selectedRun.anchor_session_id ? (
                  <div className="h-96">
                    <LiveBrowserViewer
                      taskId={selectedRun.anchor_session_id}
                      taskStatus={selectedRun.status}
                      className="w-full h-full rounded-b-lg"
                    />
                  </div>
                ) : (
                  <div className="h-96 flex items-center justify-center bg-slate-50">
                    <div className="text-center">
                      <Monitor className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">
                        {selectedRun
                          ? 'No live session available'
                          : 'Click on a running workflow to view live browser session'
                        }
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Enhanced Pagination */}
        {data?.pagination && data.pagination.total > data.pagination.limit && (
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
                  {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
                  {data.pagination.total} results
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="border-slate-300"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPage(page + 1)}
                    disabled={page * data.pagination.limit >= data.pagination.total}
                    className="border-slate-300"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
